import { NestedCheckboxes } from '@dealstack/frontend/shared/components/forms/SearchSelectWithNestedCollapsableChildren/NestedCheckboxes';
import { DisplayCaret } from '@dealstack/frontend/shared/components/forms/SearchSelectWithNestedCollapsableChildren/SelectInputDisplay';
import {
	Container,
	DisplaySelectedItemsContainer,
	NestedCheckboxContainer,
	OverlayContainer,
	SearchSelectWithNestedCollapsableChildrenContainer,
	StyledText,
	Title,
} from '@dealstack/frontend/shared/components/forms/SearchSelectWithNestedCollapsableChildren/styled';
import {
	filterDataBySearch,
	getSelectedItems,
} from '@dealstack/frontend/shared/components/forms/SearchSelectWithNestedCollapsableChildren/utils';
import { Badge, Flex, Input, Text } from '@mantine/core';
import { useClickOutside } from '@mantine/hooks';
import { castArray } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import type { SearchSelectWithNestedCollapsableChildrenOptions } from './SearchSelectWithNestedCollapsableChildren.types';

const MAX_DISPLAYED_SELECTED_LABELS = 3;

interface Props {
	options?: SearchSelectWithNestedCollapsableChildrenOptions[];
	initialSelectedValues?: SearchSelectWithNestedCollapsableChildrenOptions['value'][];
	searchPlaceHolderText?: string;
	containerWidth?: string;
	inputTitle?: string;
	placeHolderText?: string;
	allowMultiOptionSelect?: boolean;
	// react Hook Form props start
	value?: SearchSelectWithNestedCollapsableChildrenOptions['value'][];
	onChange?: (value: SearchSelectWithNestedCollapsableChildrenOptions['value'][]) => void;
	onBlur?: () => void;
	name?: string;
	// react Hook Form props end
}

export const SearchSelectWithNestedCollapsableChildren = ({
	options = [],
	initialSelectedValues = [],
	searchPlaceHolderText = 'Search',
	placeHolderText, // placeholder text
	containerWidth, // set the overall width of the container, defaults to 100%
	inputTitle, // set the title above the input
	allowMultiOptionSelect = true,
	value: controlledValue,
	onChange,
	onBlur,
	name,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const clickOutSideRef = useClickOutside(() => closeMultiSelect());

	const [expanded, setExpanded] = useState<string[]>([]);

	// Use controlled value if provided (react-hook-form), otherwise use internal state
	const [internalSelected, setInternalSelected] = useState<Set<string>>(
		new Set(initialSelectedValues.map((value) => value))
	);

	const selected = useMemo(
		() => (controlledValue ? new Set(castArray(controlledValue)) : internalSelected),
		[controlledValue, internalSelected]
	);

	const [searchText, setSearchText] = useState('');

	const toggleExpand = useCallback((label: string) => {
		setExpanded((prev) =>
			prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
		);
	}, []);

	const toggleSelection = useCallback(
		(value: string) => {
			let newSelected: Set<string>;

			if (allowMultiOptionSelect) {
				// Multi-selection mode - toggle the value
				newSelected = new Set(selected);
				newSelected.has(value) ? newSelected.delete(value) : newSelected.add(value);
			} else {
				// Single selection mode - replace current selection with new value
				newSelected = new Set([value]);
			}

			setInternalSelected(newSelected);

			// Call onChange if provided (for React Hook Form integration)
			onChange?.(Array.from(newSelected));
		},
		[selected, onChange, allowMultiOptionSelect]
	);

	const closeMultiSelect = useCallback(() => {
		setIsOpen(false);
		setSearchText('');

		// Call onBlur if provided (for React Hook Form integration)
		onBlur?.();
	}, [onBlur]);

	// get filtered data based on search
	const filteredData = useMemo(
		() => filterDataBySearch(options, searchText),
		[options, searchText]
	);

	const selectedLabels = useMemo(() => getSelectedItems(options, selected), [options, selected]);

	return (
		<Container width={containerWidth}>
			<Title>{inputTitle}</Title>
			<Flex>
				<SearchSelectWithNestedCollapsableChildrenContainer ref={clickOutSideRef}>
					<Flex>
						<DisplaySelectedItemsContainer
							onClick={() => setIsOpen(true)}
							onBlur={onBlur}
							data-testid={
								name ? `search-select-with-nested-children-${name}` : undefined
							}
							tabIndex={0}
							role="combobox"
							aria-expanded={isOpen}
							aria-haspopup="listbox"
						>
							{!selected.size && <StyledText>{placeHolderText}</StyledText>}
							{selected.size > 0 && (
								<Flex wrap="wrap" gap="xs" align="center">
									{selectedLabels
										.slice(0, MAX_DISPLAYED_SELECTED_LABELS)
										.map((item) => (
											<Badge
												key={item.id}
												size="xs"
												variant="outline"
												color="gray.4"
												c="dark"
											>
												<Text
													size="xs"
													c="dark"
													fw={500}
													style={{
														textAlign: 'center',
														whiteSpace: 'nowrap',
													}}
												>
													{item.name}
												</Text>
											</Badge>
										))}
									{selected.size > MAX_DISPLAYED_SELECTED_LABELS && (
										<Badge
											key="more-indicator"
											size="xs"
											variant="outline"
											color="gray.4"
											c="dark"
										>
											<Text size="xs" c="dark" fw={500}>
												+{selected.size - MAX_DISPLAYED_SELECTED_LABELS}{' '}
											</Text>
										</Badge>
									)}
								</Flex>
							)}
							<DisplayCaret isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} />
						</DisplaySelectedItemsContainer>
					</Flex>
					<OverlayContainer isOpen={isOpen}>
						<Input
							placeholder={searchPlaceHolderText}
							size="xs"
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
						/>

						<NestedCheckboxContainer>
							{NestedCheckboxes(
								filteredData,
								selected,
								expanded,
								toggleSelection,
								toggleExpand
							)}
						</NestedCheckboxContainer>
					</OverlayContainer>
				</SearchSelectWithNestedCollapsableChildrenContainer>
			</Flex>
		</Container>
	);
};
